import { apiClient } from './api';
import type {
  ChannelTokenBalance,
  CreditAllocation,
  CreateAllocationRequest,
  AllocationRevokeResult,
  CoachCreditCap,
  CoachCreditCapRequest,
  TokenGrantRequest,
  TokenGrantResult,
} from '@/types/api';
import type {
  Channel,
  ChannelDiscoveryResponse,
  ChannelDiscoverySort,
  ChannelImageUploadResponse,
  ChannelMember,
  ChannelMembershipSummary,
  ChannelInviteResponse,
  ChannelPendingInvitation,
  ChannelPublicResponse,
  ChannelTemplateItem,
  CoachQuotaResponse,
  CoachQuotaSetRequest,
  CommonWeaknessesResponse,
  CreateChannelRequest,
  MemberRoleChangeRequest,
  UpdateChannelRequest,
  PerTemplateAthleteResponse,
  SetRemovalAuthorizationRequest,
  SetRemovalAuthorizationResponse,
} from '@/types/channel';

/**
 * Get all channels owned by or accessible to the current user
 */
export async function getMyChannels(): Promise<Channel[]> {
  const response = await apiClient.get<Channel[]>('/users/me/channels');
  return response.data;
}

/**
 * Get a single channel by slug
 */
export async function getChannel(slug: string): Promise<Channel> {
  const response = await apiClient.get<Channel>(`/channels/${slug}`);
  return response.data;
}

/**
 * Create a new channel
 */
export async function createChannel(data: CreateChannelRequest): Promise<Channel> {
  const response = await apiClient.post<Channel>('/channels', data);
  return response.data;
}

/**
 * Update channel settings
 */
export async function updateChannel(
  slug: string,
  data: UpdateChannelRequest
): Promise<Channel> {
  const response = await apiClient.patch<Channel>(`/channels/${slug}`, data);
  return response.data;
}

/**
 * Get all members of a channel
 */
export async function getChannelMembers(slug: string): Promise<ChannelMember[]> {
  const response = await apiClient.get<ChannelMember[]>(`/channels/${slug}/members`);
  return response.data;
}

/**
 * Add a member to a channel by email
 */
export async function addChannelMember(slug: string, email: string): Promise<ChannelMember> {
  const response = await apiClient.post<ChannelMember>(`/channels/${slug}/members`, { email });
  return response.data;
}

/**
 * Remove a member from a channel
 */
export async function removeChannelMember(slug: string, userId: string): Promise<void> {
  await apiClient.delete(`/channels/${slug}/members/${userId}`);
}

/**
 * Change a member's role in the channel.
 * Owner/channel_admin only. Returns 409 when the seat cap is reached.
 */
export async function updateMemberRole(
  slug: string,
  userId: string,
  payload: MemberRoleChangeRequest,
): Promise<ChannelMember> {
  const { data } = await apiClient.patch<ChannelMember>(
    `/channels/${slug}/members/${userId}/role`,
    payload,
  );
  return data;
}

/**
 * Get all channel memberships for the current user.
 * Used by ChannelSwitcher to list every channel + the user's role in each.
 */
export async function getMyChannelMemberships(): Promise<ChannelMembershipSummary[]> {
  const { data } = await apiClient.get<ChannelMembershipSummary[]>('/auth/me/channel-memberships');
  return data;
}

/**
 * Join a public channel as the current user
 */
export async function joinChannel(slug: string): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>(`/channels/${slug}/join`);
  return response.data;
}

/** Optional params accepted by GET /channels (S20). */
export interface GetPublicChannelsParams {
  /** Page number (1-based, default 1). */
  page?: number;
  /** Results per page (1–100, default 20). */
  page_size?: number;
  /** Case-insensitive substring search on display_name. */
  q?: string;
  /** Sort order: 'newest' (default) or 'name'. */
  sort?: ChannelDiscoverySort;
  /** Case-insensitive substring filter on sport_focus. */
  sport_focus?: string;
}

/**
 * Get publicly listed (discoverable) channels — paginated envelope (S20).
 *
 * Returns a ChannelDiscoveryResponse envelope:
 *   { items, total, page, page_size, has_more }
 *
 * Items are marketing-face-only (ChannelPublicResponse) — no private fields.
 * B-2026-0105: listed==true channels only; no id/owner_id/leaderboard_enabled.
 *
 * The FULL envelope is returned so callers (S21 browse UI, D-S48 funnel) can
 * access total/has_more for pagination controls.
 */
export async function getPublicChannels(
  params?: GetPublicChannelsParams,
): Promise<ChannelDiscoveryResponse> {
  const response = await apiClient.get<ChannelDiscoveryResponse>('/channels', { params });
  return response.data;
}

// ─── Access request types and API ────────────────────────────────────────────

export type ChannelRequestedRole = 'coach' | 'athlete' | 'viewer';

export interface ChannelAccessRequest {
  id: string;
  channel_slug: string;
  requester_user_id: string;
  requester_display_name: string | null;
  requester_email: string;
  message: string | null;
  requested_role: ChannelRequestedRole;
  created_at: string;
  status: 'pending' | 'approved' | 'denied';
}

export interface CreateAccessRequestPayload {
  message?: string;
  requested_role?: ChannelRequestedRole;
}

/**
 * Submit a request to join a private channel.
 */
export async function createAccessRequest(
  slug: string,
  payload: CreateAccessRequestPayload
): Promise<ChannelAccessRequest> {
  const response = await apiClient.post<ChannelAccessRequest>(
    `/channels/${slug}/access-requests`,
    payload
  );
  return response.data;
}

/**
 * Fetch all pending access requests for a channel (owner-only).
 */
export async function getAccessRequests(slug: string): Promise<ChannelAccessRequest[]> {
  const response = await apiClient.get<ChannelAccessRequest[]>(
    `/channels/${slug}/access-requests`
  );
  return response.data;
}

/**
 * Approve a pending access request (owner-only).
 */
export async function approveAccessRequest(slug: string, requestId: string): Promise<void> {
  await apiClient.post(`/channels/${slug}/access-requests/${requestId}/approve`);
}

/**
 * Deny a pending access request (owner-only).
 */
export async function denyAccessRequest(slug: string, requestId: string): Promise<void> {
  await apiClient.post(`/channels/${slug}/access-requests/${requestId}/deny`);
}

/**
 * Withdraw the current user's own pending request to join a channel.
 */
export async function withdrawAccessRequest(slug: string): Promise<void> {
  await apiClient.delete(`/channels/${slug}/access-requests/me`);
}

// ─── Channel invite (Wave 2.2) ────────────────────────────────────────────────

/**
 * Invite a user to the channel by email.
 * If the email matches an existing registered user they are added immediately
 * (outcome = "added_existing"). Otherwise a registration invitation is created
 * and sent to that address (outcome = "invitation_sent").
 */
export async function inviteChannelMember(
  slug: string,
  email: string,
  message?: string,
  intended_role: 'athlete' | 'coach' = 'athlete',
): Promise<ChannelInviteResponse> {
  const { data } = await apiClient.post<ChannelInviteResponse>(
    `/channels/${slug}/invite`,
    { email, message, intended_role },
  );
  return data;
}

/**
 * Fetch all pending (non-expired, non-redeemed) invitations for a channel.
 * Owner-only endpoint.
 */
export async function getChannelInvitations(
  slug: string,
): Promise<ChannelPendingInvitation[]> {
  const { data } = await apiClient.get<ChannelPendingInvitation[]>(
    `/channels/${slug}/pending-invitations`,
  );
  return data;
}

/**
 * Revoke a pending registration invitation.  Backend mounts this as a
 * channel-scoped sub-resource (rather than an admin endpoint with ownership
 * check) so coach access is enforced via the channel-owner gate.
 * Returns 204 on success.
 */
export async function revokeChannelInvitation(slug: string, invitationId: string): Promise<void> {
  await apiClient.delete(`/channels/${slug}/pending-invitations/${invitationId}`);
}

/**
 * Invalidate an existing channel invitation and issue a fresh one with a new token.
 * Mirrors POST /admin/registration-invitations/{id}/resend semantics.
 * Returns the new ChannelPendingInvitation on success (201).
 * Owner-only or original creator allowed.
 */
export async function resendChannelInvitation(
  slug: string,
  invitationId: string,
): Promise<ChannelPendingInvitation> {
  const { data } = await apiClient.post<ChannelPendingInvitation>(
    `/channels/${slug}/pending-invitations/${invitationId}/resend`,
  );
  return data;
}

// ─── Per-template athlete stats (Phase C.1) ───────────────────────────────────

/**
 * Fetch per-template athlete summary stats for a channel.
 * Requires channel owner or admin. Returns 403 for non-owners.
 */
export async function getChannelTemplateAthletes(
  slug: string,
  templateId: string,
): Promise<PerTemplateAthleteResponse> {
  const { data } = await apiClient.get<PerTemplateAthleteResponse>(
    `/channels/${slug}/templates/${templateId}/athletes`,
  );
  return data;
}

// ─── Common weaknesses (Phase E.2) ───────────────────────────────────────────

/**
 * Fetch the most-common body-part weakness labels across evaluations for a
 * (channel, template) pair.  Requires channel owner or admin.
 *
 * @param limit - number of rows to return (1-25, default 5 per backend)
 */
export async function getCommonWeaknesses(
  slug: string,
  templateId: string,
  limit?: number,
): Promise<CommonWeaknessesResponse> {
  const params = limit !== undefined ? { limit } : {};
  const { data } = await apiClient.get<CommonWeaknessesResponse>(
    `/channels/${slug}/templates/${templateId}/weaknesses`,
    { params },
  );
  return data;
}

// ─── Public landing page (B-2026-0071) ───────────────────────────────────────

/**
 * Fetch the public identity + counts for a channel landing page.
 * Does NOT require authentication — callers should not pass auth headers.
 */
export async function getChannelPublic(slug: string): Promise<ChannelPublicResponse> {
  const { data } = await apiClient.get<ChannelPublicResponse>(
    `/channels/${slug}/public`,
  );
  return data;
}

// ─── Image uploads (B-2026-0071) ─────────────────────────────────────────────

/**
 * Upload a square logo image for the channel (≤ 2 MB, image/* MIME).
 * Owner only.  Sends multipart/form-data.
 */
export async function uploadChannelLogo(
  slug: string,
  file: File,
): Promise<ChannelImageUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ChannelImageUploadResponse>(
    `/channels/${slug}/logo`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * Upload a widescreen banner image for the channel (≤ 5 MB, image/* MIME).
 * Owner only.  Sends multipart/form-data.
 */
export async function uploadChannelBanner(
  slug: string,
  file: File,
): Promise<ChannelImageUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<ChannelImageUploadResponse>(
    `/channels/${slug}/banner`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

// ─── Channel template library (Phase C, B-2026-0088) ─────────────────────────

/**
 * List templates in a channel, optionally filtered to a single coach's templates.
 * Requires channel membership. Passing a coachUserId who is not a channel member
 * returns an empty list (per backend).
 */
export async function getChannelTemplates(
  slug: string,
  coachUserId?: string,
): Promise<ChannelTemplateItem[]> {
  const params = coachUserId ? { coach_user_id: coachUserId } : {};
  const { data } = await apiClient.get<ChannelTemplateItem[]>(
    `/channels/${slug}/templates`,
    { params },
  );
  return data;
}

// ─── Per-coach template quotas (Phase C, B-2026-0067) ────────────────────────
// Enterprise (T3) channels only; owner/channel_admin only.

/**
 * Fetch a sub-coach's monthly template-creation override.
 * NOTE: the backend returns 404 when NO override exists — that is the normal
 * "inherits the platform default" state, not an error. Callers should catch the
 * 404 (isAxiosError → status 404) and render the platform-default state instead
 * of surfacing an error.
 */
export async function getCoachQuota(
  slug: string,
  coachUserId: string,
): Promise<CoachQuotaResponse> {
  const { data } = await apiClient.get<CoachQuotaResponse>(
    `/channels/${slug}/coach-quotas/${coachUserId}`,
  );
  return data;
}

/**
 * Set (upsert) a sub-coach's monthly template-creation override (T3 only).
 * `templates_created_per_month` must be ≥ 0 (0 = blocked).
 */
export async function setCoachQuota(
  slug: string,
  coachUserId: string,
  payload: CoachQuotaSetRequest,
): Promise<CoachQuotaResponse> {
  const { data } = await apiClient.put<CoachQuotaResponse>(
    `/channels/${slug}/coach-quotas/${coachUserId}`,
    payload,
  );
  return data;
}

/**
 * Remove a sub-coach's override, reverting them to the platform cap (T3 only).
 * Returns 204 on success; 404 when no override existed.
 */
export async function deleteCoachQuota(slug: string, coachUserId: string): Promise<void> {
  await apiClient.delete(`/channels/${slug}/coach-quotas/${coachUserId}`);
}

// ─── S56: Member capability management ───────────────────────────────────────

/**
 * Grant or revoke the can_remove_members capability for a channel member.
 * Owner + platform admin ONLY (channel_admin → 403).
 * Targeting the owner → 400 cannot_grant_to_owner.
 *
 * PUT /channels/{slug}/members/{userId}/removal-authorization
 */
export async function setMemberRemovalAuthorization(
  slug: string,
  userId: string,
  canRemove: boolean,
): Promise<SetRemovalAuthorizationResponse> {
  const payload: SetRemovalAuthorizationRequest = { can_remove_members: canRemove };
  const { data } = await apiClient.put<SetRemovalAuthorizationResponse>(
    `/channels/${slug}/members/${userId}/removal-authorization`,
    payload,
  );
  return data;
}

// TODO S56: can_authorize_purchases toggle — wire to existing purchase-authorization
// endpoint once a frontend client function is added to this file. The backend
// endpoint already exists in credit_routes.py (set_purchase_authorization), but
// no frontend service function has been created yet. Add setPurchaseAuthorization
// here when that endpoint is surfaced to the frontend.

// ─── S26: Self-readable evaluation balance ────────────────────────────────────

/**
 * Balance response from GET /channels/{slug}/members/{userId}/evaluation-balance.
 *
 * S26 backend contract (verified):
 *   - available_evaluations: total available (included + purchased)
 *   - included_available: credits from the coach's monthly allocation
 *   - purchased_available: credits from purchased top-up packs
 *   - status: 'available' | 'no_allocation' | 'credit_balance_zero'
 *   - membership_status: 'approved' | 'pending' | 'none'
 *
 * Self-read only: a user may only fetch their own balance.
 */
export interface MemberEvaluationBalance {
  channel_id: string;
  user_id: string;
  available_evaluations: number;
  included_available: number;
  purchased_available: number;
  status: 'available' | 'no_allocation' | 'credit_balance_zero';
  membership_status: 'approved' | 'pending' | 'none';
  /**
   * True only when the credit economy is actually enforced
   * (SPORTFORM_CREDIT_LEDGER_ENABLED). When false the legacy monthly-budget path
   * governs evals and this balance is informational only — never gate submit on
   * a zero balance. Optional for backward-compat with pre-field responses
   * (treated as false when absent).
   */
  enforcement_active?: boolean;
}

/**
 * Fetch the calling user's own evaluation credit balance for a channel.
 * Self-read is allowed; attempting to read another user's balance returns 403.
 *
 * Returns null when the endpoint is unavailable (flag off / 404 / network error)
 * so callers can degrade gracefully.
 */
export async function getMemberEvaluationBalance(
  slug: string,
  userId: string,
): Promise<MemberEvaluationBalance> {
  const { data } = await apiClient.get<MemberEvaluationBalance>(
    `/channels/${slug}/members/${userId}/evaluation-balance`,
  );
  return data;
}

// ─── Credit distribution (D3, B-2026-0065) ────────────────────────────────────
// Owner / channel_admin / platform admin surfaces. Endpoints live in
// backend/app/api/credit_routes.py.

/**
 * Channel pool balance + per-coach allocation rollup (owner/channel_admin).
 * Returns 404 when the channel does not exist; callers should treat a 404 as
 * "credit system not enabled / no pool" and degrade gracefully.
 */
export async function getTokenBalance(slug: string): Promise<ChannelTokenBalance> {
  const { data } = await apiClient.get<ChannelTokenBalance>(
    `/channels/${slug}/token-balance`,
  );
  return data;
}

/**
 * List all credit allocations in the channel (owner/channel_admin).
 * Optional comma-separated status filter (e.g. 'active').
 */
export async function getTokenAllocations(
  slug: string,
  status?: string,
): Promise<CreditAllocation[]> {
  const params = status ? { status } : {};
  const { data } = await apiClient.get<CreditAllocation[]>(
    `/channels/${slug}/token-allocations`,
    { params },
  );
  return data;
}

/**
 * Authorise a member (or the whole channel via a null recipient) to draw credits.
 */
export async function createTokenAllocation(
  slug: string,
  body: CreateAllocationRequest,
): Promise<CreditAllocation> {
  const { data } = await apiClient.post<CreditAllocation>(
    `/channels/${slug}/token-allocations`,
    body,
  );
  return data;
}

/**
 * Revoke (void) an allocation's unspent credits.
 * Partly-consumed allocations require force=true (only the unspent portion is voided).
 */
export async function revokeTokenAllocation(
  slug: string,
  allocationId: string,
  force = false,
): Promise<AllocationRevokeResult> {
  const params = force ? { force: true } : {};
  const { data } = await apiClient.delete<AllocationRevokeResult>(
    `/channels/${slug}/token-allocations/${allocationId}`,
    { params },
  );
  return data;
}

/**
 * Set (upsert) a coach's credit-allocation cap (owner/channel_admin).
 * NOTE: the backend route is /channels/{slug}/coach-credit-caps/{coachId}.
 */
export async function setCoachCreditCap(
  slug: string,
  coachId: string,
  body: CoachCreditCapRequest,
): Promise<CoachCreditCap> {
  const { data } = await apiClient.put<CoachCreditCap>(
    `/channels/${slug}/coach-credit-caps/${coachId}`,
    body,
  );
  return data;
}

/**
 * List every coach allocation cap in the channel (owner/channel_admin).
 */
export async function listCoachCreditCaps(slug: string): Promise<CoachCreditCap[]> {
  const { data } = await apiClient.get<CoachCreditCap[]>(
    `/channels/${slug}/coach-credit-caps`,
  );
  return data;
}

/**
 * Deposit credits into the channel pool (PLATFORM ADMIN ONLY, pre-Stripe).
 */
export async function depositCredits(
  slug: string,
  body: TokenGrantRequest,
): Promise<TokenGrantResult> {
  const { data } = await apiClient.post<TokenGrantResult>(
    `/channels/${slug}/token-grant`,
    body,
  );
  return data;
}
